export type Lang = "en" | "de";

type Dict = Record<string, string>;

// Keep keys stable. If a key is missing in the selected language, the English value is used.
export const translations: Record<Lang, Dict> = {
  en: {
    "lang.en": "EN",
    "lang.de": "DE",

    // Footer
    "footer.rights": "takeKONTROL © 2024 · All rights reserved",

    // Nav
    "nav.controlPanel": "Control Panel",
    "nav.profileSetup": "Profile Setup",
    "nav.profile": "Profile",
    "nav.journeyPillars": "Journey Pillars",
    "nav.labScore": "LAB Score",
    "nav.actionPlan": "Action Plan",
    "nav.groupView": "Group View",
    "nav.managerView": "Manager View",
    "nav.loading": "Loading…",
    "nav.menu": "Menu",
    "nav.help": "Help",

    // Entry
    "entry.brand": "takeKONTROL",
    "entry.email.title": "Enter your company email",
    "entry.email.placeholder": "Company email",
    "entry.email.continue": "Continue",
    "entry.email.register": "Register yourself",

    "entry.password.login.title": "Enter your password",
    "entry.password.login.cta": "Log in",
    "entry.password.forgot": "Forgot your password?",
    "entry.password.useDifferent": "Use a different email",
    "entry.password.placeholder": "Password",

    "entry.password.set.title": "Set your password",
    "entry.password.set.cta": "Create account",
    "entry.back": "Back",

    "entry.register.title": "Create your account",
    "entry.register.cta": "Create account",
    "entry.register.companyPlaceholder": "Company name",
    "entry.register.noCompanies": "No companies found",
    "entry.register.departmentPlaceholder": "Department",
    "entry.register.anonymousToggle": "Register anonymously",
    "entry.register.firstName": "First name",
    "entry.register.lastName": "Last name",
    "entry.register.positionPlaceholder": "Position",
    "entry.register.personalAddress": "Personal address",
    "entry.register.consentPrefix": "I agree to the ",
    "entry.register.consentJoin": " and ",
    "entry.register.privacyPolicy": "Privacy Policy",
    "entry.register.termsConditions": "Terms & Conditions",

    "entry.reset.title": "Reset password",
    "entry.reset.subtitle": "Enter your email and we'll send you a reset link.",
    "entry.reset.cta": "Send reset link",
    "entry.reset.backToLogin": "Back to login",

    "entry.confirm.title": "Confirm your email",
    "entry.confirm.subtitle": "We sent you a confirmation link. Please check your inbox and confirm your email address before logging in.",
    "entry.confirm.backToLogin": "Back to login",
    "entry.confirm.resend": "Resend confirmation",

    "entry.loading": "Loading…",

    // Errors / info
    "entry.err.invalidEmail": "Please enter a valid company email.",
    "entry.err.invalidEmailDomain": "We couldn't verify that email's domain. Please double-check it for typos.",
    "entry.err.notFound": "Email not found in company records.",
    "entry.err.edgeInvoke": "Failed to send a request to the Edge Function.",
    "entry.err.noPassword": "Please enter your password.",
    "entry.err.linkFailed": "Login worked, but linking failed.",
    "entry.err.pwTooShort": "Password must be at least 8 characters.",
    "entry.err.companyRequired": "Please enter your company.",
    "entry.err.departmentRequired": "Please enter your department.",
    "entry.err.nameRequired": "Please enter your first and last name.",
    "entry.err.positionRequired": "Please enter your position.",
    "entry.err.personalAddressRequired": "Please enter your personal address.",
    "entry.err.legalRequired": "Please accept the Privacy Policy and Terms & Conditions to continue.",
    "entry.err.userExists": "This email already exists. Please log in instead.",
    "entry.err.selfRegisterFailed": "We couldn't create your account. Please try again.",
    "entry.alert.userExists": "This email already exists. Please log in instead.",
    "entry.alert.checkEmail": "Please check your email to confirm your account, then log in.",
    "entry.info.checkEmailConfirm": "Check your email to confirm your account, then log in.",
    "entry.reset.invalidEmail": "Please enter a valid email.",
    "entry.reset.sent": "If this email exists, a reset link was sent. Please check your inbox.",
  },
  de: {
    "lang.en": "EN",
    "lang.de": "DE",

    // Footer
    "footer.rights": "takeKONTROL © 2024 · Alle Rechte vorbehalten",

    // Nav
    "nav.ControlPanel": "Control Panel",
    "nav.profileSetup": "Profileinstellungen",
    "nav.profile": "Profil",
    "nav.journeyPillars": "Journey Säulen",
    "nav.labScore": "LAB-Score",
    "nav.actionPlan": "Aktionsplan",
    "nav.groupView": "Gruppen-Ansicht",
    "nav.managerView": "Manager-Ansicht",
    "nav.loading": "Lädt…",
    "nav.menu": "Menü",
    "nav.help": "Hilfe",

    // Entry
    "entry.brand": "takeKONTROL",
    "entry.email.title": "Gib deine Firmen-E-Mail ein",
    "entry.email.placeholder": "Firmen-E-Mail",
    "entry.email.continue": "Weiter",
    "entry.email.register": "Selbst registrieren",

    "entry.password.login.title": "Gib dein Passwort ein",
    "entry.password.login.cta": "Anmelden",
    "entry.password.forgot": "Passwort vergessen?",
    "entry.password.useDifferent": "Andere E-Mail verwenden",
    "entry.password.placeholder": "Passwort",

    "entry.password.set.title": "Passwort festlegen",
    "entry.password.set.cta": "Konto erstellen",
    "entry.back": "Zurück",

    "entry.register.title": "Konto erstellen",
    "entry.register.cta": "Konto erstellen",
    "entry.register.companyPlaceholder": "Firmenname",
    "entry.register.noCompanies": "Keine Firmen gefunden",
    "entry.register.departmentPlaceholder": "Abteilung",
    "entry.register.anonymousToggle": "Anonym registrieren",
    "entry.register.firstName": "Vorname",
    "entry.register.lastName": "Nachname",
    "entry.register.positionPlaceholder": "Position",
    "entry.register.personalAddress": "Persönliche Adresse",
    "entry.register.consentPrefix": "Ich akzeptiere die ",
    "entry.register.consentJoin": " und die ",
    "entry.register.privacyPolicy": "Datenschutzerklärung",
    "entry.register.termsConditions": "AGB",

    "entry.reset.title": "Passwort zurücksetzen",
    "entry.reset.subtitle": "Gib deine E-Mail ein und wir senden dir einen Reset-Link.",
    "entry.reset.cta": "Reset-Link senden",
    "entry.reset.backToLogin": "Zurück zur Anmeldung",

    "entry.confirm.title": "Bestätigen Sie Ihre E-Mail",
    "entry.confirm.subtitle": "Wir haben Ihnen einen Bestätigungslink gesendet. Bitte überprüfen Sie Ihren Posteingang und bestätigen Sie Ihre E-Mail-Adresse, bevor Sie sich anmelden.",
    "entry.confirm.backToLogin": "Zurück zur Anmeldung",
    "entry.confirm.resend": "Bestätigung erneut senden",

    "entry.loading": "Lädt…",

    // Errors / info
    "entry.err.invalidEmail": "Bitte gib eine gültige Firmen-E-Mail ein.",
    "entry.err.invalidEmailDomain": "Wir konnten die Domain dieser E-Mail nicht verifizieren. Bitte auf Tippfehler prüfen.",
    "entry.err.notFound": "Diese E-Mail wurde in den Firmendaten nicht gefunden.",
    "entry.err.edgeInvoke": "Anfrage an die Edge Function ist fehlgeschlagen.",
    "entry.err.noPassword": "Bitte gib dein Passwort ein.",
    "entry.err.linkFailed": "Anmeldung erfolgreich, aber Verknüpfung ist fehlgeschlagen.",
    "entry.err.pwTooShort": "Das Passwort muss mindestens 8 Zeichen lang sein.",
    "entry.err.companyRequired": "Bitte gib deine Firma ein.",
    "entry.err.departmentRequired": "Bitte gib deine Abteilung ein.",
    "entry.err.nameRequired": "Bitte gib Vor- und Nachnamen ein.",
    "entry.err.positionRequired": "Bitte gib deine Position ein.",
    "entry.err.personalAddressRequired": "Bitte gib deine persönliche Adresse ein.",
    "entry.err.legalRequired": "Bitte akzeptiere die Datenschutzerklärung und die AGB, um fortzufahren.",
    "entry.err.userExists": "Diese E-Mail existiert bereits. Bitte melde dich an.",
    "entry.err.selfRegisterFailed": "Konto konnte nicht erstellt werden. Bitte versuche es erneut.",
    "entry.alert.userExists": "Diese E-Mail existiert bereits. Bitte melde dich an.",
    "entry.alert.checkEmail": "Bitte bestätige dein Konto per E-Mail und melde dich danach an.",
    "entry.info.checkEmailConfirm": "Bitte bestätige dein Konto per E-Mail und melde dich danach an.",
    "entry.reset.invalidEmail": "Bitte gib eine gültige E-Mail ein.",
    "entry.reset.sent": "Falls diese E-Mail existiert, wurde ein Reset-Link gesendet. Bitte prüfe deinen Posteingang.",
  },
};

export function translate(lang: Lang, key: string): string {
  return translations[lang][key] ?? translations.en[key] ?? key;
}
